import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { inheritanceClaimsService, InheritanceClaimSummary } from '../../services/inheritanceClaims.service'
import toast from 'react-hot-toast'

const STATUS_FILTERS = ['PENDING', 'UNDER_REVIEW', 'APPROVED', 'PAID', 'REJECTED'] as const

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  UNDER_REVIEW: 'bg-blue-100 text-blue-800',
  APPROVED: 'bg-emerald-100 text-emerald-800',
  PAID: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
}

export default function InheritanceClaimsPage() {
  const navigate = useNavigate()
  const [claims, setClaims] = useState<InheritanceClaimSummary[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [filter, setFilter] = useState<string>('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await inheritanceClaimsService.list(filter || undefined)
      setClaims(data.claims)
      setCounts(data.counts)
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load claims')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    load()
  }, [load])

  const statusPill = (status: string) => (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_COLORS[status] ?? 'bg-neutral-100 text-neutral-700'}`}>
      {status.replace('_', ' ')}
    </span>
  )

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="mx-auto max-w-6xl px-6">
        <h1 className="text-2xl font-bold text-neutral-900">Inheritance Claims</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Verify death certificates against the member's will, then distribute the wallet balance.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('')}
            className={`rounded-full px-3 py-1 text-xs font-medium ${filter === '' ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-600 ring-1 ring-neutral-200'}`}
          >
            All ({Object.values(counts).reduce((s, n) => s + n, 0)})
          </button>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${filter === s ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-600 ring-1 ring-neutral-200'}`}
            >
              {s.replace('_', ' ')} ({counts[s] ?? 0})
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {loading ? (
            <p className="text-sm text-neutral-500">Loading…</p>
          ) : claims.length === 0 ? (
            <div className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
              No claims{filter ? ` with status ${filter}` : ''}.
            </div>
          ) : (
            claims.map((claim) => (
              <button
                key={claim.id}
                onClick={() => navigate(`/admin/claims/${claim.id}`)}
                className="block w-full rounded-xl border border-neutral-200 bg-white p-4 text-left hover:border-neutral-300"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-mono text-sm font-semibold text-neutral-900">{claim.claimNumber}</p>
                    <p className="text-xs text-neutral-500">
                      Account {claim.deceasedAccountId} ·{' '}
                      {claim.user ? `${claim.user.firstName} ${claim.user.lastName}`.trim() : ''} · claimant{' '}
                      {claim.submittedByName} ({claim.submittedByMemberId})
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {claim.balanceAtApproval && (
                      <span className="text-sm text-neutral-700">RM {Number(claim.balanceAtApproval).toFixed(2)}</span>
                    )}
                    {statusPill(claim.status)}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
